/**
 * OAuth Configuration for WayFong Authentication
 *
 * This file contains configuration for the OAuth gate that validates
 * users are from authorized domains (wayfong.com/wallyco.net) before
 * allowing access to the TnB application.
 */

// Environment variable controls whether OAuth is enabled
// Set VITE_OAUTH_ENABLED=false to bypass OAuth for local development
export const OAUTH_ENABLED = import.meta.env.VITE_OAUTH_ENABLED !== 'false';

// WayFong Auth service URL
export const WAYFONG_AUTH_URL = import.meta.env.VITE_WAYFONG_AUTH_URL || 'https://auth.wallyco.net';

// WCO-2914 — HTE permission enforcement (per-env rollout switch). When true, a user who
// signs in but is NOT granted HTE access (`hte:<env>:*:access`) is blocked on every page with an
// access-denied screen instead of entering. OFF by default → reverts to the domain-only
// gate (rollback = set this false).
export const HTE_ACCESS_ENFORCE = import.meta.env.VITE_HTE_ACCESS_ENFORCE === 'true';

// OAuth endpoints
// Token validation and logout are proxied through our backend to avoid CORS issues
export const OAUTH_ENDPOINTS = {
  // Google OAuth initiation endpoint (direct - browser redirects here)
  GOOGLE_AUTH: `${WAYFONG_AUTH_URL}/auth/google`,
  // Token validation endpoint - proxied through our backend
  TOKEN_VALIDATE: '/api/auth/oauth/validate',
  // Logout endpoint - proxied through our backend
  LOGOUT: '/api/auth/oauth/logout',
};

// Local storage keys
export const OAUTH_STORAGE_KEYS = {
  // Token from WayFong Auth service
  ACCESS_TOKEN: 'wayfongOAuthToken',
  // User info from OAuth validation
  USER_INFO: 'wayfongOAuthUser',
  // Intended destination before OAuth redirect
  REDIRECT_URL: 'oauthRedirectUrl',
};

// Hard cap on how far ahead the scheduler will wait when the stored expiry is
// unrealistically distant. Also doubles as a safety-net upper bound for the
// expiry-aware scheduler in useOAuth.
export const TOKEN_VALIDATION_INTERVAL_MS = 11 * 60 * 60 * 1000;

// Fallback poll interval used when no `expires_in` was returned by the upstream
// (legacy session or upstream omission). Short enough to catch a dead token
// within a few minutes rather than waiting hours.
export const TOKEN_VALIDATION_FALLBACK_MS = 5 * 60 * 1000;

// How long before the stored expiry we re-validate. Picking the next check ~60s
// before the boundary lets us catch an actually-expiring token without thrashing.
export const TOKEN_VALIDATION_LEAD_MS = 60 * 1000;

// OAuth callback path in the TnB app
export const OAUTH_CALLBACK_PATH = '/oauth/callback';
